import "./home.css";

import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import {
  SERVICE_CATALOG,
  filterCatalog,
  flattenServices,
  getCategoryTeaser,
  isCabBookingService,
  slugify
} from "../../vyntezbuddy/serviceCatalog";

import SiteFooter from "../../vyntezbuddy/SiteFooter";
import SiteHeader from "../../vyntezbuddy/SiteHeader";
import { getImageForKey } from "../../vyntezbuddy/serviceImagery";
import { useNavigate } from "react-router-dom";
import { useVBTranslation } from "../../vyntezbuddy/language";

const HERO_FRAMES = [
  {
    title: "Cab Booking",
    subtitle: "Move fast with curated travel, cab, and driver services.",
    image: getImageForKey("hero-cab-booking", 1200, 720)
  },
  {
    title: "Home Services",
    subtitle: "Repairs, cleaning, and maintenance at the doorstep.",
    image: getImageForKey("hero-home-services", 1200, 720)
  },
  {
    title: "Healthcare",
    subtitle: "Doctors, nurses, diagnostics, and care support.",
    image: getImageForKey("hero-healthcare", 1200, 720)
  },
  {
    title: "Business Services",
    subtitle: "Build, scale, and organize your business faster.",
    image: getImageForKey("hero-business-services", 1200, 720)
  }
];

const TRUST_METRICS = [
  { value: "25+", label: "Service categories" },
  { value: "1200+", label: "Active providers" },
  { value: "98%", label: "On-time assistance" },
  { value: "4.7/5", label: "Customer satisfaction" }
];

const WORKFLOW_STEPS = [
  {
    title: "Discover",
    detail: "Search by category, service type, or locality to quickly shortlist providers."
  },
  {
    title: "Compare",
    detail: "Review service scope, availability, and profile details before confirming a request."
  },
  {
    title: "Book",
    detail: "Submit requests, track activity in dashboard, and keep your addresses ready for faster checkout."
  }
];

const CUSTOMER_PROMISES = [
  "Verified provider onboarding with profile-based service mapping",
  "Location-aware discovery with address and map support",
  "Consumer and provider journeys built in one streamlined platform",
  "Responsive experience for mobile-first and desktop-first usage"
];

const INSIGHT_NOTES = [
  {
    title: "Before you book",
    text: "Compare provider scope and availability first to avoid scheduling conflicts."
  },
  {
    title: "Location-ready requests",
    text: "Keep your saved addresses updated so providers can estimate and respond faster."
  },
  {
    title: "Profile-first trust",
    text: "Verified details and clear communication reduce back-and-forth during service delivery."
  }
];

const OFFERING_PRIORITIES = [
  {
    title: "Everyday Essentials",
    detail: "Fast-moving services for home upkeep and daily support.",
    category: "Home Services"
  },
  {
    title: "Mobility and Travel",
    detail: "Cab and travel help tailored for planned and urgent rides.",
    category: "Travel"
  },
  {
    title: "Care and Wellness",
    detail: "Health and personal care services delivered with dependable coordination.",
    category: "Health"
  }
];

const EXPERIENCE_PILLARS = [
  {
    title: "Family Care Planner",
    detail: "Bundle routine home, repair, and wellness services into one dependable flow.",
    sectionTag: "For Households",
    targetCategory: "Home Services"
  },
  {
    title: "Business Support Hub",
    detail: "Coordinate staffing, transport, and operations support for daily business continuity.",
    sectionTag: "For Businesses",
    targetCategory: "Business"
  },
  {
    title: "Travel Confidence Lane",
    detail: "Book travel and mobility support with transparent discovery and faster turnaround.",
    sectionTag: "For Mobility",
    targetCategory: "Travel"
  }
];

const Home = () => {
  const navigate = useNavigate();
  const { t } = useVBTranslation();
  const [search, setSearch] = useState("");
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setFrameIndex((index) => (index + 1) % HERO_FRAMES.length);
    }, 4200);

    return () => window.clearInterval(timer);
  }, []);

  const filteredCatalog = useMemo(() => filterCatalog(SERVICE_CATALOG, search), [search]);
  const resultCount = useMemo(() => flattenServices(filteredCatalog).length, [filteredCatalog]);

  const popularServices = useMemo(() => {
    const picks = SERVICE_CATALOG.map((section) => ({
      category: section.category,
      accent: section.accent,
      service: section.services[0]
    }));
    return [...picks, ...picks];
  }, []);

  const offeringCards = useMemo(() => {
    return OFFERING_PRIORITIES.map((item) => {
      const matchedCategory = filteredCatalog.find((section) => section.category === item.category);
      return {
        ...item,
        accent: matchedCategory?.accent || "#0f766e",
        services: matchedCategory?.services.slice(0, 4) || [],
        totalServices: matchedCategory?.services.length || 0,
        target: matchedCategory?.category || item.category
      };
    });
  }, [filteredCatalog]);

  const experienceCards = useMemo(() => {
    return EXPERIENCE_PILLARS.map((pillar) => {
      const linked = filteredCatalog.find((section) => section.category === pillar.targetCategory);
      return {
        ...pillar,
        accent: linked?.accent || "#0f766e",
        services: linked?.services.slice(0, 3) || [],
        target: linked?.category || pillar.targetCategory
      };
    });
  }, [filteredCatalog]);

  return (
    <div className="ab-home-page">
      <SiteHeader search={{ value: search, onChange: setSearch }} />

      <main className="ab-home-main">
        <section className="ab-hero-shell">
          <motion.div
            className="ab-hero-copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="ab-section-tag">Consumer + Provider marketplace</p>
            <h2>Professional local services, designed for everyday life.</h2>
            <p className="ab-hero-text">
              Vyntez Buddy helps families and businesses discover trusted providers across home care,
              transport, health, and specialist services with a clean, transparent, and reliable booking flow.
            </p>

            <div className="ab-hero-actions">
              <button type="button" className="vb-btn vb-btn-primary" onClick={() => navigate("/login")}>
                Start Secure Login
              </button>
              <button type="button" className="vb-btn vb-btn-secondary" onClick={() => navigate("/provider-register")}>
                Become a Provider
              </button>
            </div>

            <div className="ab-hero-badges">
              <span>Service matching by category</span>
              <span>Address-ready booking experience</span>
              <span>Fast OTP authentication</span>
              <span>Built for consumers and providers</span>
            </div>

            <div className="ab-metric-strip">
              {TRUST_METRICS.map((item) => (
                <article key={item.label}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </article>
              ))}
            </div>
          </motion.div>

          <div className="ab-hero-carousel">
            <AnimatePresence mode="wait">
              <motion.article
                key={frameIndex}
                className="ab-carousel-card"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.38 }}
              >
                <img src={HERO_FRAMES[frameIndex].image} alt={HERO_FRAMES[frameIndex].title} />
                <div className="ab-carousel-overlay">
                  <p>Featured service lane</p>
                  <h2>{HERO_FRAMES[frameIndex].title}</h2>
                  <span>{HERO_FRAMES[frameIndex].subtitle}</span>
                </div>
              </motion.article>
            </AnimatePresence>

            <div className="ab-carousel-dots">
              {HERO_FRAMES.map((frame, index) => (
                <button
                  key={frame.title}
                  type="button"
                  className={index === frameIndex ? "active" : ""}
                  onClick={() => setFrameIndex(index)}
                  aria-label={frame.title}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="ab-journey-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">How it works</p>
            </div>
            <span>Inspired by high-clarity marketplaces, adapted for Vyntez Buddy workflows.</span>
          </div>

          <div className="ab-journey-grid">
            {WORKFLOW_STEPS.map((step, index) => (
              <article key={step.title} className="ab-journey-card">
                <small>Step {index + 1}</small>
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ab-experience-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Curated experiences</p>
            </div>
          </div>

          <div className="ab-experience-grid">
            {experienceCards.map((item, index) => (
              <motion.article
                key={item.title}
                className="ab-experience-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.25, delay: index * 0.05 }}
              >
                <header>
                  <small>{item.sectionTag}</small>
                  <span style={{ background: item.accent }} />
                </header>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
                <div className="ab-experience-tags">
                  {item.services.map((service) => (
                    <b key={`${item.title}-${service}`}>{service}</b>
                  ))}
                </div>
                <button type="button" className="ab-text-btn" onClick={() => navigate(`/services/${slugify(item.target)}`)}>
                  Open {item.target}
                </button>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="ab-marquee-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Services</p>
            </div>
          </div>

          <div className="ab-marquee-track">
            <div className="ab-marquee-row">
              {popularServices.map((item, index) => (
                <button
                  type="button"
                  key={`${item.category}-${item.service}-${index}`}
                  className="ab-marquee-card"
                  onClick={() =>
                    isCabBookingService(item.category, item.service)
                      ? navigate("/cab-booking")
                      : navigate(`/services/${slugify(item.category)}/${slugify(item.service)}`)
                  }
                >
                  <img src={getImageForKey(`${item.category}-${item.service}`)} alt={item.service} />
                  <div className="ab-marquee-card-body">
                    <span style={{ background: item.accent }} />
                    <div>
                      <strong>{item.service}</strong>
                      <small>{item.category}</small>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="ab-offer-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Consumer Centric</p>
            </div>
          </div>

          <div className="ab-offer-grid">
            {offeringCards.map((item) => (
              <article className="ab-offer-card" key={item.title}>
                <div className="ab-offer-head">
                  <h3>{item.title}</h3>
                  <span style={{ background: item.accent }}>{item.totalServices} services</span>
                </div>
                <p>{item.detail}</p>

                <div className="ab-offer-tags">
                  {item.services.map((service) => (
                    <small key={`${item.title}-${service}`}>{service}</small>
                  ))}
                </div>

                <button
                  type="button"
                  className="ab-text-btn"
                  onClick={() => navigate(`/services/${slugify(item.target)}`)}
                >
                  View {item.target}
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="ab-category-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Categories</p>
            </div>
            <span>
              {t("homeServicesMeta", {
                services: resultCount,
                categories: filteredCatalog.length
              })}
            </span>
          </div>

          <div className="ab-category-grid">
            {filteredCatalog.map((section, index) => (
              <motion.article
                key={section.category}
                className="ab-category-card"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.25, delay: index * 0.02 }}
              >
                <img
                  src={HERO_FRAMES[index % HERO_FRAMES.length].image}
                  alt={section.category}
                />
                <div className="ab-category-body">
                  <div className="ab-category-row">
                    <h3>{section.category}</h3>
                    <span style={{ background: section.accent }} />
                  </div>
                  <small className="ab-category-count">{section.services.length} services available</small>
                  <p>{getCategoryTeaser(section)}</p>
                  <div className="ab-category-tags">
                    {section.services.slice(0, 4).map((service) => (
                      <span key={`${section.category}-${service}`}>{service}</span>
                    ))}
                    {section.services.length > 4 && <span>+{section.services.length - 4}</span>}
                  </div>
                  <button type="button" className="ab-text-btn" onClick={() => navigate(`/services/${slugify(section.category)}`)}>
                    Explore Category
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="ab-assurance-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Platform assurance</p>
            </div>
          </div>

          <ul className="ab-assurance-list">
            {CUSTOMER_PROMISES.map((promise) => (
              <li key={promise}>{promise}</li>
            ))}
          </ul>
        </section>

        <section className="ab-insights-section">
          <div className="ab-section-head">
            <div>
              <p className="ab-section-tag">Service intelligence</p>
            </div>
          </div>

          <div className="ab-insights-grid">
            {INSIGHT_NOTES.map((item) => (
              <article key={item.title} className="ab-insight-card">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Home;
